/**
 * Breadcrumb derivation.
 *
 * Single source of truth for "where am I?" — the TopBar reads from here,
 * detail pages set the trailing label via the BreadcrumbContext (see
 * `BreadcrumbProvider`).
 *
 * Rules:
 *   1. Exact path match in SECTIONS → use that section's group + label.
 *   2. Otherwise, longest-prefix match against SECTIONS paths so that
 *      `/build/servers/abc123` resolves to the "Publisher Workspace"
 *      section.
 *   3. A handful of known detail routes (servers/:id, catalog/toolkits/
 *      :slug, access/request) carry an explicit parent override.
 */
import { SECTIONS, type Section } from './sections.js';

interface DetailRoute {
  readonly prefix: string; // e.g. "/build/servers/"
  readonly parentPath: string; // e.g. "/build/publisher"
  readonly defaultLabel: string; // shown until the page sets a real label
}

const DETAIL_ROUTES: readonly DetailRoute[] = [
  { prefix: '/build/servers/', parentPath: '/build/publisher', defaultLabel: 'Server detail' },
  { prefix: '/catalog/toolkits/', parentPath: '/catalog', defaultLabel: 'Toolkit detail' },
  { prefix: '/access/request', parentPath: '/access', defaultLabel: 'Request access' },
];

export interface Crumb {
  readonly label: string;
  readonly path?: string; // omit for the current/leaf crumb
}

export interface BreadcrumbChain {
  readonly group: string;
  readonly crumbs: readonly Crumb[];
}

export function breadcrumbForPath(pathname: string, detailLabel?: string): BreadcrumbChain {
  // 1) exact match
  const exact = SECTIONS.find((s) => s.path === pathname);
  if (exact) {
    return {
      group: exact.group,
      crumbs: [{ label: exact.label }],
    };
  }

  // 2) detail route (server, toolkit, access request)
  const detail = DETAIL_ROUTES.find((d) => pathname.startsWith(d.prefix));
  if (detail) {
    const parent = SECTIONS.find((s) => s.path === detail.parentPath);
    if (parent) {
      return {
        group: parent.group,
        crumbs: [
          { label: parent.label, path: parent.path },
          { label: detailLabel || detail.defaultLabel },
        ],
      };
    }
  }

  // 3) longest-prefix match against any section
  const sorted = [...SECTIONS]
    .filter((s) => s.path !== '/' && pathname.startsWith(s.path))
    .sort((a, b) => b.path.length - a.path.length);
  const prefix: Section | undefined = sorted[0];
  if (prefix) {
    return {
      group: prefix.group,
      crumbs: [
        { label: prefix.label, path: prefix.path },
        {
          label: detailLabel || pathname.slice(prefix.path.length).replace(/^\/+/, '') || 'Detail',
        },
      ],
    };
  }

  // 4) fallback
  return { group: 'Platform', crumbs: [{ label: 'Console' }] };
}
